import { useState, type FormEvent } from 'react';

import { APP_CONFIG } from '../config';
import { format } from '../i18n';
import { useStrings } from '../i18n/context';
import type { LanguageCode } from '../languages';
import { readPref } from '../prefs';
import type { Route } from '../routing';
import { DeviceCheck, type DeviceChoices } from './DeviceCheck';
import { LanguageSelect } from './LanguageSelect';
import { OpenInNewTab } from './OpenInNewTab';
import { PrivacyNotice } from './PrivacyNotice';

export const NAME_MAX_LENGTH = 48; // as token/lib/validate.js

export interface JoinChoices {
  readonly name: string;
  readonly language: LanguageCode;
  readonly devices: DeviceChoices;
}

export type JoinError = 'name' | 'connect' | 'rateLimited';

interface Props {
  readonly route: Route;
  readonly language: LanguageCode;
  readonly onLanguageChange: (code: LanguageCode) => void;
  /** Called from the submit handler, inside the click: it must unlock audio before awaiting. */
  readonly onJoin: (choices: JoinChoices) => Promise<JoinError | null>;
  readonly embedded: boolean;
}

function cleanName(value: string): string | null {
  const name = value.normalize('NFC').replace(/\s+/gu, ' ').trim();
  const length = [...name].length;
  return length > 0 && length <= NAME_MAX_LENGTH ? name : null;
}

export function Lobby({ route, language, onLanguageChange, onJoin, embedded }: Props) {
  const strings = useStrings();
  const [name, setName] = useState(() => readPref('name') ?? '');
  const [devices, setDevices] = useState<DeviceChoices>({
    cameraOn: true,
    microphoneOn: true,
    cameraId: '',
    microphoneId: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<JoinError | null>(null);

  const errorText: Record<JoinError, string> = {
    name: strings.errorName,
    connect: strings.errorConnect,
    rateLimited: strings.errorRateLimited,
  };

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const cleaned = cleanName(name);
    if (cleaned === null) {
      setError('name');
      return;
    }
    setError(null);
    setBusy(true);
    // onJoin runs its first steps synchronously, still inside the user's click.
    void onJoin({ name: cleaned, language, devices }).then((failure) => {
      if (failure) {
        setError(failure);
        setBusy(false);
      }
    });
  }

  return (
    <main className="lobby" aria-labelledby="vm-lobby-heading">
      <div className="lobby__card">
        {embedded && <OpenInNewTab />}
        <p className="brand lobby__brand">
          <img className="brand__mark" src={`${APP_CONFIG.basePath}brand/logo-mark.svg`} alt="" width="44" height="41" />
          <span className="brand__name">{strings.appTitle}</span>
        </p>
        <h1 id="vm-lobby-heading">{route.code ? strings.lobbyJoinHeading : strings.lobbyNewHeading}</h1>
        {route.code && <p className="lobby__code">{format(strings.lobbyMeetingCode, { code: route.code })}</p>}
        {route.invalidCode && (
          <p role="alert" className="notice notice--error">
            {strings.errorInvalidLink}
          </p>
        )}

        <form className="lobby__form" onSubmit={submit} noValidate>
          <div className="field">
            <label htmlFor="vm-name">{strings.nameLabel}</label>
            <input
              id="vm-name"
              name="name"
              type="text"
              autoComplete="name"
              maxLength={NAME_MAX_LENGTH}
              required
              value={name}
              aria-invalid={error === 'name'}
              aria-describedby="vm-name-hint"
              onChange={(event) => setName(event.target.value)}
            />
            <p id="vm-name-hint" className="field__hint">
              {strings.nameHint}
            </p>
          </div>

          <LanguageSelect value={language} onChange={onLanguageChange} />
          <DeviceCheck value={devices} onChange={setDevices} />
          <PrivacyNotice />

          {error && (
            <p role="alert" className="notice notice--error">
              {errorText[error]}
            </p>
          )}
          <button type="submit" className="button button--primary" disabled={busy} aria-busy={busy}>
            {busy ? strings.connecting : route.code ? strings.joinMeeting : strings.startMeeting}
          </button>
        </form>
      </div>
    </main>
  );
}
